import React from "react";
import { useTranslation } from "../../context/TranslationContext";
import { isTaxIncluded } from "../../services/taxApi";

const BookingSummary = ({
  selectedRooms,
  selectedAddOns,
  roomSubtotal,
  addOnsSubtotal,
  taxes,
  total,
  nights,
  selectedDates,
  guests,
  addOnComments,
  removeRoom,
  removeAddOn,
  startCheckout,
  calculateNights,
  accommodationName,
  currentCurrency,
}) => {
  const { t } = useTranslation();
  // Get currency from RatePlanCurrencyCode > CurrencyCode > currentCurrency (same as RatePlanCard)
  const codeToSymbol = (code) => {
    if (!code || typeof code !== 'string') return '';
    const symbols = {
      'INR': '₹', 'USD': '$', 'EUR': '€', 'GBP': '£', 'AED': 'AED',
      'SAR': 'SAR', 'QAR': 'QAR', 'KWD': 'KWD', 'OMR': 'OMR', 'BHD': 'BHD',
      'PHP': '₱', 'THB': '฿', 'MYR': 'RM', 'SGD': 'S$', 'IDR': 'Rp',
      'PKR': 'Rs'
    };
    return symbols[code.toUpperCase()] || code;
  };
  const firstRatePlan = selectedRooms?.[0]?.ratePlan || {};
  const convertedRateVal = firstRatePlan.convertedRate ? parseFloat(firstRatePlan.convertedRate) : 0;
  const useConverted = convertedRateVal > 0 && typeof firstRatePlan.convertedCurrency === 'string' && firstRatePlan.convertedCurrency.trim();
  const rawCode = useConverted ? firstRatePlan.convertedCurrency :
                  (firstRatePlan.ratePlanCurrencyCode || firstRatePlan.RatePlanCurrencyCode ||
                  firstRatePlan.CurrencyCode || firstRatePlan.currency ||
                  currentCurrency?.code || '');
  const rawSymbol = rawCode ? codeToSymbol(rawCode) : (currentCurrency?.code ? codeToSymbol(currentCurrency.code) : (currentCurrency?.symbol || ''));
  const currencySymbol = rawSymbol.length > 1 ? rawSymbol + ' ' : rawSymbol;
  // Calculate totals directly from selectedRooms - no memoization to avoid stale values
  const getCalculatedTotals = () => {
    // If no selectedRooms, use props as fallback
    if (!selectedRooms || !Array.isArray(selectedRooms) || selectedRooms.length === 0) {
      const fallbackRooms = Number(roomSubtotal) || 0;
      const fallbackTaxes = Number(taxes) || 0;
      const fallbackAddOns = Number(addOnsSubtotal) || 0;
      return {
        roomsTotal: fallbackRooms,
        taxesTotal: fallbackTaxes,
        addOnsTotal: fallbackAddOns
      };
    }

    // Calculate rooms total with quantity - sum ALL selected rooms
    let roomsTotal = 0;
    let taxesTotal = 0;

    selectedRooms.forEach((item) => {
      if (!item || !item.ratePlan || !item.room) return;

      const qty = Number(item.quantity) || 1;

      // Calculate price - use calculatedPrice if available (includes loyalty discount)
      let price = item.calculatedPrice;
      if (!price) {
        price = item.ratePlan.price || (item.room.basePrice * (1 + (item.ratePlan.priceModifier || 0)));
        if (item.ratePlan.promotionDiscount?.discount > 0) {
          price = item.ratePlan.promotionDiscount.afterDiscountAmount;
        }
        if (item.ratePlan.secretDeal) {
          price = price * (1 - (item.ratePlan.memberDiscount || 0));
        }
      }

      roomsTotal += price * qty;

      // Calculate taxes from ratePlan.taxes (stored from tax API)
      if (item.ratePlan.taxes && Array.isArray(item.ratePlan.taxes)) {
        item.ratePlan.taxes.forEach((tax) => {
          if (isTaxIncluded(tax)) return; // Included tax = already in rate, don't add
          const taxId = tax.TaxId || tax.taxId || tax.TaxID || 0;
          let taxAmt = 0;
          if (taxId === 19) {
            taxAmt = parseFloat(tax.TaxValue || tax.taxValue || 0);
          } else {
            taxAmt = parseFloat(tax.TaxAmmount || tax.TaxAmount || tax.taxAmount || 0);
          }
          if (!isNaN(taxAmt)) taxesTotal += taxAmt;
        });
      } else if (item.taxAmount && typeof item.taxAmount === 'number' && !isNaN(item.taxAmount)) {
        taxesTotal += item.taxAmount;
      }
    });

    const addOnsTotal = Number(addOnsSubtotal) || 0;

    return {
      roomsTotal: roomsTotal,
      taxesTotal: taxesTotal,
      addOnsTotal: addOnsTotal
    };
  };

  // Calculate totals once and use everywhere
  const totals = getCalculatedTotals();
  const displayRooms = Number(totals.roomsTotal) || 0;
  const displayTaxes = Number(totals.taxesTotal) || 0;
  const displayAddOns = Number(totals.addOnsTotal) || 0;
  const displayTotal = displayRooms + displayTaxes + displayAddOns;


  return (
    <div className="lg:col-span-1">
        
      <div className="bg-white shadow-2xl border border-gray-100 rounded-2xl lg:sticky lg:top-12 flex flex-col h-[calc(100vh-6rem)] overflow-hidden">
        
        {/* Header */}
        <div className="card-head px-6 py-4 flex-shrink-0">
          <h3 className="text-lg font-semibold text-white tracking-wide">
            {accommodationName}
          </h3>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6 min-h-0">
          {/* Dates & Guests */}
          <div className="space-y-3 bg-gradient-to-br from-gray-50 to-white p-4 rounded-xl border border-gray-200 shadow-sm">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">{t('bookingSummary.checkIn')}</span>
              <span className="font-medium text-gray-900">
                {selectedDates.checkIn
                  ? new Date(selectedDates.checkIn).toLocaleDateString("en-US", {
                      weekday: "short",
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    })
                  : "--"}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">{t('bookingSummary.checkOut')}</span>
              <span className="font-medium text-gray-900">
                {selectedDates.checkOut
                  ? new Date(selectedDates.checkOut).toLocaleDateString(
                      "en-US",
                      {
                        weekday: "short",
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                      }
                    )
                  : "--"}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">{t('search.guests')}</span>
              <span className="font-medium text-gray-900">
                {guests.adults} {guests.adults > 1 ? t('search.adults') : t('search.adult')},{" "}
                {guests.children} {guests.children !== 1 ? t('search.children') : t('search.child')}
              </span>
            </div>
          </div>

          <hr />

          {selectedRooms.length > 0 || selectedAddOns.length > 0 ? (
            <>
              {/* Summary Header */}
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-xl font-bold text-gray-900">
                    {t('bookingSummary.bookingSummary')}
                  </h4>
                  <p className="text-xs text-gray-500">
                    {selectedRooms.length > 0 &&
                      t('bookingSummary.roomsPlans', { rooms: selectedRooms.reduce((sum, item) => sum + (Number(item.quantity) || 1), 0), plans: selectedRooms.length })}
                    {selectedRooms.length > 0 &&
                      selectedAddOns.length > 0 &&
                      " • "}
                    {selectedAddOns.length > 0 &&
                      t('bookingSummary.addOnsCount', { count: selectedAddOns.reduce((sum, item) => sum + (Number(item.quantity) || 1), 0) })}
                  </p>
                </div>
              </div>

              {/* Checkout CTA */}
            

              {/* Room Cards */}
              {Array.from(new Set(selectedRooms.map((i) => i.room.id))).map(
                (roomId) => {
                  const roomItems = selectedRooms.filter(
                    (i) => i.room?.id === roomId
                  );
                  if (!roomItems.length) return null;
                  const room = roomItems[0].room;

                  return (
                    <div
                      key={roomId}
                      className="bg-gradient-to-br from-white to-gray-100 border border-gray-200 rounded-lg p-4 shadow-sm"
                    >
                      <h5 className="text-sm font-semibold text-gray-800 mb-2">
                        {room.name}
                      </h5>
                      <div className="space-y-2">
                        {roomItems.map((item) => (
                          <div
                            key={`${item.room.id}-${item.ratePlan.id}`}
                            className="flex items-start justify-between bg-white rounded-lg p-2 text-xs border border-gray-100 gap-2"
                          >
                            <span className="font-medium text-gray-700 min-w-0 flex-1">
                              {item.ratePlan?.name || 'Rate Plan'}
                            </span>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <span className="text-green-600 font-semibold whitespace-nowrap">
                                {currencySymbol}{(() => {
                                  const qty = Number(item.quantity) || 1;
                                  const nightsCount = nights || calculateNights();
                                  // Use calculatedPrice if available (includes loyalty discount), otherwise calculate
                                  let price = item.calculatedPrice;
                                  if (!price) {
                                    // Fallback: calculate price if calculatedPrice not stored
                                    price = item.ratePlan?.price || ((item.room?.basePrice || 0) * (1 + (item.ratePlan?.priceModifier || 0)));
                                    if (item.ratePlan?.promotionDiscount?.discount > 0) {
                                      price = item.ratePlan.promotionDiscount.afterDiscountAmount;
                                    }
                                    if (item.ratePlan?.secretDeal) {
                                      price = price * (1 - (item.ratePlan?.memberDiscount || 0));
                                    }
                                  }
                                  return (Number(price) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                                })()}
                              </span>
                              <button
                                onClick={() => removeRoom(item.room.id, item.ratePlan.id)}
                                className="flex items-center justify-center 
                                          text-[#a0895b] hover:text-[#8a7549] 
                                          transition-colors duration-200 
                                          focus:outline-none p-1"
                                title="Remove Plan"
                              >
                                <svg
                                  className="w-3.5 h-3.5"
                                  fill="currentColor"
                                  viewBox="0 0 20 20"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                }
              )}

              {/* Add-ons */}
              {selectedAddOns.length > 0 && (
                <div className="bg-gradient-to-br from-white to-gray-100 border border-gray-200 rounded-lg p-4 shadow-sm">
                  <h5 className="text-sm font-semibold text-gray-800 mb-2">
                    {t('bookingSummary.addOns').replace(':', '')}
                  </h5>
                  <div className="space-y-2">
                    {selectedAddOns.map((item) => (
                      <div
                        key={item.addOn.id}
                        className="flex items-center bg-white rounded-lg p-2 text-xs border border-gray-100 "
                      >
                        <button
                          onClick={() => removeAddOn(item.addOn.id, item.option?.id)}
                          className="w-6 h-6 flex items-center justify-center 
                                    text-white hover:text-white 
                                    avablity-btn hover:opacity-90 
                                    rounded-full transition-colors duration-200 
                                    focus:outline-none focus:ring-2 focus:ring-opacity-50 mr-2"
                          title="Remove Add-on"
                        >
                          <svg
                            className="w-3.5 h-3.5"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </button>
                        <div className="justify-between w-full flex items-center p-1">
                          <span className="font-medium text-gray-700">
                            {item.addOn.name}
                            {item.option && (
                              <span className="text-xs text-gray-500 ml-1">
                                ({item.option.name})
                              </span>
                            )}
                          </span>
                          <span className="text-green-600 font-semibold">
                            {(() => {
                              const addonCode = item.addOn?.currencyCode || '';
                              const rawSym = addonCode ? codeToSymbol(addonCode) : currencySymbol;
                              const addonSymbol = rawSym.length > 1 ? rawSym + ' ' : rawSym;
                              const basePrice = item.option?.price || item.addOn?.price || 0;
                              const qty = item.quantity || 1;
                              const nightsMultiplier = String(item.addOn?.activityId) === '4' ? (nights || 1) : 1;
                              return `${addonSymbol}${(basePrice * qty * nightsMultiplier).toLocaleString()}`;
                            })()}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Price Breakdown */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-2">
                {displayRooms > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">{t('bookingSummary.rooms')}</span>
                    <span className="font-medium">
                      {currencySymbol}{displayRooms.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                )}
                {displayAddOns > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">{t('bookingSummary.addOns')}</span>
                    <span className="font-medium">
                      {(() => {
                        const addonCode = selectedAddOns?.[0]?.addOn?.currencyCode || '';
                        const rawAddonSym = addonCode ? codeToSymbol(addonCode) : currencySymbol;
                        const addonSym = rawAddonSym.length > 1 ? rawAddonSym + ' ' : rawAddonSym;
                        return `${addonSym}${displayAddOns.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                      })()}
                    </span>
                  </div>
                )}
                {displayTaxes > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">{t('bookingSummary.taxesAndFees')}</span>
                  <span className="font-medium">
                    {currencySymbol}{displayTaxes.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
                )}
                <hr />
                <div className="flex justify-between text-lg font-bold">
                  <span>{t('bookingSummary.totalAmount')}:</span>
                  <span className="text-inherit" data-total={displayTotal}>
                    {(() => {
                      // Use addon currency if only addons selected, otherwise room currency
                      const hasRooms = displayRooms > 0;
                      const addonCode = selectedAddOns?.[0]?.addOn?.currencyCode || '';
                      const rawTotalSym = hasRooms ? currencySymbol : (addonCode ? codeToSymbol(addonCode) : currencySymbol);
                      const totalSym = rawTotalSym.length > 1 ? rawTotalSym + ' ' : rawTotalSym;
                      return `${totalSym}${(displayRooms + displayTaxes + displayAddOns).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                    })()}
                  </span>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-12 text-gray-500">
                  <div className="text-gray-400 mb-4">
                                <svg
                                    className="h-16 w-16 mx-auto"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                >
                                    <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3z" />
                                </svg>
                            </div>
              <p className="text-lg font-medium">{t('bookingSummary.noItemsSelected')}</p>
              <p className="text-sm">{t('bookingSummary.selectRoomsAndAddOns')}</p>
            </div>
          )}
        </div>

        {/* Fixed Button at Bottom */}
        {(selectedRooms.length > 0 || selectedAddOns.length > 0) && (
          <div className="flex-shrink-0 border-t border-gray-200 bg-white p-6 pt-4">
            <button
              onClick={startCheckout}
              className="w-full avablity-btn text-white font-semibold py-3 rounded-xl transition-all duration-300 transform hover:scale-[1.02] shadow-md hover:shadow-lg focus:ring-4 focus:ring-emerald-300 flex items-center justify-center gap-2"
            >
              <svg
                className="h-5 w-5"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              {t('bookingSummary.proceedToCheckout')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default BookingSummary;
